import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit2, Trash2, Image as ImageIcon, Check, X } from 'lucide-react';
import './ProductsManagement.css';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    quantity: '',
    tasteProfile: '',
    nutritionalBenefits: '',
    growingTime: '',
    isAvailable: true
  });

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/products`, {
        withCredentials: true
      });
      setProducts(response.data.products);
    } catch (error) {
      if (error.response?.status === 403) {
        alert('Admin access required');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from name
    if (name === 'name' && !editingProduct) {
      const slug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('slug', formData.slug);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('quantity', formData.quantity);
      formDataToSend.append('tasteProfile', formData.tasteProfile);
      formDataToSend.append('growingTime', formData.growingTime);
      formDataToSend.append('isAvailable', formData.isAvailable);

      // Handle nutritional benefits as array
      const benefits = formData.nutritionalBenefits.split(',').map(b => b.trim()).filter(Boolean);
      benefits.forEach(benefit => {
        formDataToSend.append('nutritionalBenefits', benefit);
      });

      if (imageFile) {
        formDataToSend.append('file', imageFile);
      }

      if (editingProduct) {
        await axios.put(`${API}/api/admin/products/${editingProduct.id}`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert('Product updated successfully!');
      } else {
        await axios.post(`${API}/api/admin/products`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert('Product created successfully!');
      }

      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      description: product.description,
      price: product.price,
      quantity: product.quantity,
      tasteProfile: product.tasteProfile || '',
      nutritionalBenefits: product.nutritionalBenefits?.join(', ') || '',
      growingTime: product.growingTime || '',
      isAvailable: product.isAvailable !== false
    });
    setImagePreview(product.image);
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/api/admin/products/${productId}`, {
        withCredentials: true
      });
      alert('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      price: '',
      quantity: '',
      tasteProfile: '',
      nutritionalBenefits: '',
      growingTime: '',
      isAvailable: true
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingProduct(null);
  };

  if (loading) {
    return <div className="admin-loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="products-management">
      <div className="page-header">
        <h1>Products Management</h1>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <Plus size={20} />
          Add New Product
        </button>
      </div>

      <div className="products-grid">
        {products.map(product => (
          <div key={product.id} className="product-card-admin">
            <div className="product-image-admin">
              <img src={product.image} alt={product.name} />
              {!product.isAvailable && (
                <div className="unavailable-badge">Unavailable</div>
              )}
            </div>
            <div className="product-info-admin">
              <h3>{product.name}</h3>
              <p className="product-price">₹{product.price}</p>
              <p className="product-quantity">{product.quantity}</p>
            </div>
            <div className="product-actions">
              <button
                className="btn-icon edit"
                onClick={() => handleEdit(product)}
                title="Edit"
              >
                <Edit2 size={18} />
              </button>
              <button
                className="btn-icon delete"
                onClick={() => handleDelete(product.id)}
                title="Delete"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
              <div className="image-upload-section">
                <label className="image-upload-label">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="image-preview" />
                  ) : (
                    <div className="image-placeholder">
                      <ImageIcon size={48} />
                      <span>Click to upload image</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Slug *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    disabled={!!editingProduct}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (₹) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="text"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    placeholder="e.g., 125g"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Taste Profile</label>
                <input
                  type="text"
                  name="tasteProfile"
                  value={formData.tasteProfile}
                  onChange={handleInputChange}
                  placeholder="e.g., Mildly sweet, grassy"
                />
              </div>

              <div className="form-group">
                <label>Nutritional Benefits (comma-separated)</label>
                <input
                  type="text"
                  name="nutritionalBenefits"
                  value={formData.nutritionalBenefits}
                  onChange={handleInputChange}
                  placeholder="e.g., High in Vitamin C, Rich in Iron"
                />
              </div>

              <div className="form-group">
                <label>Growing Time</label>
                <input
                  type="text"
                  name="growingTime"
                  value={formData.growingTime}
                  onChange={handleInputChange}
                  placeholder="e.g., 7-10 days"
                />
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="isAvailable"
                    checked={formData.isAvailable}
                    onChange={handleInputChange}
                  />
                  <span>Product is available for sale</span>
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create Product')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsManagement;